import clsx from 'clsx';
import { useState } from 'react';
import { FiGift, FiLock } from 'react-icons/fi';

import { AnchoredPopover } from '@/components/AnchoredPopover/AnchoredPopover';
import ButtonSmall from '@/components/Button/ButtonSmall';
import { SEAT_POOL_LABEL } from '@/pages/SubscriptionSettingsPage/vessel-seats';
import { GATE_LABEL, type GateReason, type PermissionGates } from '@/permissions/permission-gates';
import { PERMISSIONS, type PermissionCategory } from '@/permissions/permissions.catalog';

import { getCategoryMeta, getPermissionMeta, hasPermissionMeta } from '../../permissions.ui';
import PermissionRow from '../PermissionRow/PermissionRow';

import s from './PermissionGroupCard.module.scss';

/** The popover's fixed wording per wall; both actions lead to the same subscriptions section. */
const UNLOCK_COPY: Record<GateReason, { eyebrow: string; action: string }> = {
  plan: { eyebrow: 'Upgrade My Plan', action: 'Upgrade Plan' },
  seat: { eyebrow: GATE_LABEL.seat, action: 'Manage Seats' },
};

interface Props {
  category: PermissionCategory;
  permissions: ReadonlySet<string>;
  excluded: ReadonlySet<string>;
  onToggle: (name: string, enabled: boolean) => void;
  search?: string;
  gates: PermissionGates;
  /** Omitted when there is nowhere to upgrade — the strip is then left off. */
  onUpgrade?: () => void;
  /**
   * The vessel is on a lifetime plan an admin granted, which cannot be upgraded: the strip
   * says so in place of the upgrade button.
   */
  planGranted?: boolean;
  /** Why a row is read-only, used as its hover text; "All"/"None" reaches only the rest. */
  lockedReasonFor?: (name: string) => string | undefined;
}

export default function PermissionGroupCard({
  category,
  permissions,
  excluded,
  onToggle,
  search,
  gates,
  onUpgrade,
  planGranted = false,
  lockedReasonFor,
}: Props) {
  const [unlock, setUnlock] = useState<{
    anchor: HTMLElement;
    reason: GateReason;
    name?: string;
  } | null>(null);
  const query = search?.trim().toLowerCase() ?? '';
  const names = Object.values(PERMISSIONS[category])
    .filter((n) => !excluded.has(n))
    .filter((n) => hasPermissionMeta(n))
    .filter((n) => {
      if (!query) return true;
      const meta = getPermissionMeta(n);
      return (
        n.toLowerCase().includes(query) ||
        meta.title.toLowerCase().includes(query) ||
        meta.description.toLowerCase().includes(query)
      );
    });
  if (names.length === 0) return null;

  /**
   * Gated rows are left out of the count and out of "All"/"None" both: the server refuses
   * to change them, so counting them would make the tally read as something the owner
   * could act on. `x/y` is therefore "of what you can control here".
   */
  const open = names.filter((n) => !gates.reasonFor(n));
  const planLocked = names.filter(
    (n) => gates.reasonFor(n) === 'plan' && gates.unlockableByUpgrade(n),
  );
  const seatLocked = names.filter((n) => gates.reasonFor(n) === 'seat');
  // A person's seat, not the plan, can be what closes the whole card; say which.
  const onlySeatGated = seatLocked.length === names.length;
  const enabledCount = open.filter((n) => permissions.has(n)).length;
  const editable = open.filter((n) => !lockedReasonFor?.(n));
  const allEnabled = editable.every((n) => permissions.has(n));
  const meta = getCategoryMeta(category);
  // Something here is behind the plan and there is somewhere to buy it: every lock label offers it.
  const canUnlock = planLocked.length > 0 && !planGranted && !!onUpgrade;
  // Seats are reassigned where the plan is bought, and a lifetime plan's seats cannot be.
  const canMoveSeat = seatLocked.length > 0 && !planGranted && !!onUpgrade;
  const headerUnlockable = onlySeatGated ? canMoveSeat : canUnlock;
  const summaryOf = (locked: string[]) =>
    open.length === 0
      ? meta.title
      : `${locked.length} more ${meta.title} ${locked.length === 1 ? 'permission' : 'permissions'}`;
  const lockedSummary = summaryOf(planLocked);
  const toggleUnlock = (anchor: HTMLElement, reason: GateReason, name?: string) =>
    setUnlock((prev) => (prev?.anchor === anchor ? null : { anchor, reason, name }));
  const unlockReason = unlock?.reason ?? 'plan';
  const unlockPool = unlockReason === 'seat' ? seatLocked : planLocked;
  // The row that opened the popover leads its list, so it never hides inside "and N more".
  const unlockNames = unlock?.name
    ? [unlock.name, ...unlockPool.filter((n) => n !== unlock.name)]
    : unlockPool;
  const unlockText =
    unlockReason === 'seat'
      ? `Add ${summaryOf(seatLocked)} by moving this person to a ${SEAT_POOL_LABEL.management} seat.`
      : `Add ${lockedSummary} with a higher plan.`;

  const handleToggleAll = () => {
    const target = !allEnabled;
    editable.forEach((n) => {
      if (permissions.has(n) !== target) onToggle(n, target);
    });
  };

  return (
    <div className={s.card}>
      <div className={s.header}>
        <div className={s.titleBlock}>
          <span className={s.title}>{meta.title}</span>
          <span className={s.description}>{meta.description}</span>
        </div>
        {headerUnlockable && open.length === 0 ? (
          <button
            type="button"
            className={clsx(s.planChip, s.upgradeBtn)}
            aria-haspopup="dialog"
            onClick={(e) => toggleUnlock(e.currentTarget, onlySeatGated ? 'seat' : 'plan')}
          >
            <FiLock className={s.planChipIcon} aria-hidden="true" />
            {onlySeatGated ? GATE_LABEL.seat : 'Upgrade to Unlock'}
          </button>
        ) : open.length === 0 ? (
          <span className={s.planChip}>
            <FiLock className={s.planChipIcon} />
            {onlySeatGated ? GATE_LABEL.seat : 'Not on your plan'}
          </span>
        ) : (
          <div className={s.headerActions}>
            <span className={s.count}>
              {enabledCount}/{open.length}
            </span>
            {editable.length > 0 && (
              <button
                type="button"
                className={s.toggleAll}
                onClick={handleToggleAll}
                aria-label={allEnabled ? 'Disable All' : 'Enable All'}
              >
                {allEnabled ? 'None' : 'All'}
              </button>
            )}
          </div>
        )}
      </div>
      <div className={s.rows}>
        {names.map((name) => {
          const gate = gates.reasonFor(name);
          const unlockable =
            (gate === 'plan' && canUnlock && planLocked.includes(name)) ||
            (gate === 'seat' && canMoveSeat);
          return (
            <PermissionRow
              key={name}
              name={name}
              enabled={permissions.has(name)}
              gate={gate}
              onUnlock={unlockable ? (anchor) => toggleUnlock(anchor, gate, name) : undefined}
              lockedReason={lockedReasonFor?.(name)}
              onChange={(enabled) => onToggle(name, enabled)}
            />
          );
        })}
      </div>
      {planLocked.length > 0 && planGranted && (
        <div className={s.upgradeStrip}>
          <span className={s.upgradeText}>
            {open.length === 0
              ? `${meta.title} isn’t part of your lifetime plan, and you can’t upgrade a lifetime plan.`
              : `${planLocked.length} more ${meta.title} ${
                  planLocked.length === 1 ? 'permission isn’t' : 'permissions aren’t'
                } part of your lifetime plan, and a lifetime plan can’t be upgraded`}
          </span>
          <span className={s.planChip}>
            <FiGift className={s.planChipIcon} />
            Lifetime Plan
          </span>
        </div>
      )}
      {canUnlock && (
        <div className={s.upgradeStrip}>
          <span className={s.upgradeText}>Unlock {lockedSummary} with a higher plan</span>
          <button type="button" className={s.upgradeBtn} onClick={onUpgrade}>
            Upgrade Plan
          </button>
        </div>
      )}
      {(canUnlock || canMoveSeat) && (
        <AnchoredPopover
          open={!!unlock}
          onClose={() => setUnlock(null)}
          anchorEl={unlock?.anchor ?? null}
          label={`Unlock ${meta.title}`}
          width={280}
        >
          <div className={s.unlock}>
            <span className={s.unlockEyebrow}>{UNLOCK_COPY[unlockReason].eyebrow}</span>
            <span className={s.unlockTitle}>Unlock {meta.title}</span>
            <span className={s.unlockText}>{unlockText}</span>
            <ul className={s.unlockList}>
              {unlockNames.slice(0, 3).map((n) => (
                <li key={n}>{getPermissionMeta(n).title}</li>
              ))}
              {unlockNames.length > 3 && <li>and {unlockNames.length - 3} more</li>}
            </ul>
            <ButtonSmall
              className={s.unlockAction}
              onClick={() => {
                setUnlock(null);
                onUpgrade?.();
              }}
            >
              {UNLOCK_COPY[unlockReason].action}
            </ButtonSmall>
          </div>
        </AnchoredPopover>
      )}
    </div>
  );
}
